// ABOUTME: UI component for individual SharePoint file/folder items in the browser modal
// ABOUTME: Displays file info, icons, breadcrumbs and action buttons with proper styling
/* eslint-disable react/prop-types */

import {
    AddIcon,
    Box,
    Button,
    Checkbox,
    ChevronRightIcon,
    FolderIcon,
    Grid,
    ImageIcon,
    InsertDriveFileIcon,
    PictureAsPdfIcon,
    Stack,
    TableChartIcon,
    Typography,
} from '@ds/coolshop';
import React from 'react';
import { useIntl } from 'react-intl';

import type { ISharePointItem } from '../api/types';
import { isSharePointFolder } from '../api/types';
import { getFileTypeLabel } from '../utils/formatters';

type IFileListItemProps = {
    readonly item: ISharePointItem;
    readonly isSearchMode: boolean;
    readonly onItemClick: (item: ISharePointItem) => void;
    readonly isBulkMode?: boolean;
    readonly isSelected?: boolean;
    readonly hasChildrenSelected?: boolean;
    readonly onSelectionChange?: (item: ISharePointItem, selected: boolean) => void;
    readonly showCheckbox?: boolean;
};

const getFileIcon = (item: ISharePointItem): React.ReactElement => {
    if (isSharePointFolder(item)) {
        return <FolderIcon style={{ color: '#1976d2', fontSize: '18px' }} />;
    }

    const fileName = item.name.toLowerCase();
    if (fileName.endsWith('.pdf')) {
        return <PictureAsPdfIcon style={{ color: '#d32f2f', fontSize: '18px' }} />;
    }
    if (fileName.endsWith('.csv')) {
        return <TableChartIcon style={{ color: '#388e3c', fontSize: '18px' }} />;
    }
    if (fileName.match(/\.(jpg|jpeg|png|gif)$/i)) {
        return <ImageIcon style={{ color: '#1976d2', fontSize: '18px' }} />;
    }
    return <InsertDriveFileIcon style={{ color: '#757575', fontSize: '18px' }} />;
};

const getIconBackgroundColor = (item: ISharePointItem): string => {
    if (isSharePointFolder(item)) return '#e3f2fd';

    const fileName = item.name.toLowerCase();
    if (fileName.endsWith('.pdf')) return '#ffebee';
    if (fileName.endsWith('.csv')) return '#e8f5e8';
    if (fileName.match(/\.(jpg|jpeg|png|gif)$/i)) return '#e3f2fd';
    return '#f5f5f5';
};

export const FileListItem: React.FC<IFileListItemProps> = React.memo(
    ({
        item,
        isSearchMode,
        onItemClick,
        isBulkMode = false,
        isSelected = false,
        hasChildrenSelected = false,
        onSelectionChange,
        showCheckbox = true,
    }) => {
        const intl = useIntl();

        return (
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                borderBottom="1px solid #f0f0f0"
                padding="12px 16px"
            >
                <Grid container spacing={2}>
                    {isBulkMode && showCheckbox && (
                        <Grid item xs={1} display="flex" alignItems="center" pl="8px">
                            <Checkbox
                                checked={isSelected}
                                onChange={e => onSelectionChange?.(item, e.target.checked)}
                                size="small"
                                indeterminate={hasChildrenSelected}
                            />
                        </Grid>
                    )}
                    <Grid item sm={isBulkMode && showCheckbox ? 3 : 4}>
                        <Box display="flex">
                            <Box
                                width="32px"
                                height="32px"
                                borderRadius="50%"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                marginRight="12px"
                                bgcolor={getIconBackgroundColor(item)}
                            >
                                {getFileIcon(item)}
                            </Box>
                            <Box textAlign="left">
                                <Typography variant="caption" color="text.secondary">
                                    {isSharePointFolder(item)
                                        ? intl.formatMessage({
                                              id: 'sharepoint.folder',
                                              defaultMessage: 'Cartella',
                                          })
                                        : getFileTypeLabel(item.name, intl)}
                                </Typography>
                                <Stack direction="row" alignItems="baseline" gap={0.5}>
                                    <Typography variant="body2">{item.name}</Typography>
                                    {isSharePointFolder(item) && !isSearchMode && (
                                        <Typography variant="caption" color="text.secondary">
                                            {`${item.childCount} `}
                                            {intl.formatMessage({
                                                id: 'sharepoint.items',
                                                defaultMessage: 'elementi',
                                            })}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item sm={6}>
                        {isSearchMode && item.breadcrumbs && item.breadcrumbs.length > 0 && (
                            <>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {intl.formatMessage({
                                            id: 'sharepoint.position',
                                            defaultMessage: 'Posizione',
                                        })}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {item.breadcrumbs.map(crumb => crumb.name).join(' / ')}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Grid>
                    <Grid
                        item
                        sm={2}
                        textAlign="right"
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                    >
                        {(!isBulkMode || // Always show button in normal mode
                            !showCheckbox || // Show button when checkbox is not shown
                            isSharePointFolder(item)) && ( // Always show OPEN button for folders
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => onItemClick(item)}
                                endIcon={
                                    isSharePointFolder(item) ? (
                                        <ChevronRightIcon fontSize="small" />
                                    ) : (
                                        <AddIcon fontSize="small" />
                                    )
                                }
                            >
                                {isSharePointFolder(item)
                                    ? intl.formatMessage({
                                          id: 'sharepoint.open',
                                          defaultMessage: 'APRI',
                                      })
                                    : intl.formatMessage({
                                          id: 'sharepoint.select',
                                          defaultMessage: 'SELEZIONA',
                                      })}
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Box>
        );
    },
);

FileListItem.displayName = 'FileListItem';
